import { Button, ErrorMessage, Flex, LabeledData, ModalV2, Text } from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";

export type ApproveForGpModalOwner = {
  id: string;
  fullName: string | null;
};

export type ApproveForGpModalProps = {
  variant?: "ddqRequest" | "backfill" | "originalFormat";
  owners: ApproveForGpModalOwner[];
  errorMessage: string | null;
  isSubmitting: boolean;
  onHide: () => void;
  onConfirm: () => void;
};

// Owners are informational only here — approveDdqForGp emails whoever setDdqProdOpsRequestOwners
// already stored; it does not set or change ownership itself, so there is nothing to edit. Neither
// the backfill nor originalFormat variants have owners to email: completeBackfillDdq emails the
// uploading GP directly, and Original Format requests have no owners concept at all (see DDQ-322's
// backend plan) — approveDdqForGp is reused unchanged there, but owner_user_ids is always empty so
// no email actually goes out.
export const ApproveForGpModal = ({
  variant = "ddqRequest",
  owners,
  errorMessage,
  isSubmitting,
  onHide,
  onConfirm,
}: ApproveForGpModalProps) => (
  <ModalV2
    show
    onHide={onHide}
    title={variant === "backfill" ? "Approve?" : "Approve for GP?"}
    size="small"
    data-testid="ddq-approve-for-gp-modal"
  >
    <ModalV2.Body>
      <Flex flexDirection="column" gap={SPACING.LARGE}>
        <Text variant="secondary-md">
          {variant === "backfill"
            ? "This will complete the review and promote the included answers into the Answer Bank."
            : variant === "originalFormat"
              ? "This will complete the review and mark the completed DDQ ready to send to the GP."
              : "This will complete the review and email the request's owners that the DDQ is ready."}
        </Text>
        {variant === "ddqRequest" && (
          <LabeledData
            variant="standard"
            flippedLabel
            label="Owners"
            value={
              owners.length > 0
                ? owners.map((owner) => owner.fullName ?? "Unnamed user").join(", ")
                : "No owners set"
            }
            data-testid="ddq-approve-for-gp-owners"
          />
        )}
        {errorMessage && (
          <ErrorMessage data-testid="ddq-approve-for-gp-error">{errorMessage}</ErrorMessage>
        )}
      </Flex>
    </ModalV2.Body>
    <ModalV2.Footer>
      <Flex flexDirection="row" gap={SPACING.SMALL} justifyContent="flex-end">
        <Button variant="neutral" onClick={onHide} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          isLoading={isSubmitting}
          disabled={isSubmitting}
          onClick={onConfirm}
          data-testid="ddq-approve-for-gp-confirm"
        >
          {variant === "backfill" ? "Approve" : "Approve for GP"}
        </Button>
      </Flex>
    </ModalV2.Footer>
  </ModalV2>
);
